import { useTranslation, Trans } from "i18n";
import { useRouteError } from "react-router-dom";
import { Button, Link } from "ui";
import { auth } from "matchi-auth";
import { ERROR_MESSAGES } from "@/utils/errors";
import { createSupportFormLink } from "@/utils/createSupportFormLink";
import * as pkg from "@/../package.json";

type RouteError = Error & {
  statusText?: string;
  status?: number;
};

interface ErrorBoundaryProps {
  errorMessage?: string;
}

export const ErrorBoundary = ({ errorMessage }: ErrorBoundaryProps) => {
  const error = useRouteError() as RouteError;
  const message = errorMessage ?? error?.message ?? "Unknown error occurred";
  const token = auth.getIdTokenParsed();
  const url = window?.location?.origin + window?.location?.pathname;
  const { t } = useTranslation();

  const supportFormLink = createSupportFormLink(
    token?.email,
    token?.sub,
    token?.locale,
    url,
  );

  if (message === ERROR_MESSAGES.LOCAL_STORAGE_DISABLED) {
    return (
      <ErrorMessage
        title={t("Cookies are required to complete the payment")}
        description={
          <Trans
            i18nKey="error.cookies.description"
            components={{
              1: <Link href={supportFormLink} target="_blank" />,
            }}
          />
        }
        message={message}
      />
    );
  }

  if (message === ERROR_MESSAGES.INVALID_USER) {
    return (
      <ErrorMessage
        title={t("We were unable to process your order")}
        description={
          <Trans
            i18nKey="error.invalidUser.description"
            components={{
              1: <Link href={supportFormLink} target="_blank" />,
            }}
          />
        }
        message={message}
        action={
          <Button onClick={auth.logout} block>
            {t("Logout")}
          </Button>
        }
      />
    );
  }

  if (message === ERROR_MESSAGES.MEMBERSHIP_INVOICED) {
    return (
      <ErrorMessage
        title={t("We were unable to process your order")}
        description={t(
          "This membership has been invoiced by the venue, the membership needs to be paid through the invoice. If you haven't received your invoice, please reach out to the venue.",
        )}
        message={message}
        action={
          <Button onClick={auth.logout} block>
            {t("Logout")}
          </Button>
        }
      />
    );
  }

  return (
    <ErrorMessage
      title={t("Something went wrong")}
      description={
        <Trans
          i18nKey="error.generic.description"
          components={{
            1: <Link href={supportFormLink} target="_blank" />,
          }}
        />
      }
      message={message}
    />
  );
};

interface ErrorMessageProps {
  title: React.ReactNode;
  description: React.ReactNode;
  message: string;
  action?: React.ReactNode;
}

const ErrorMessage = ({
  title,
  description,
  message,
  action,
}: ErrorMessageProps) => {
  const token = auth.getIdTokenParsed();
  const path = window?.location?.href
    ? new URL(window.location.href)?.pathname
    : null;

  return (
    <div>
      <h1 className="mb-2 text-lg font-medium">{title}</h1>
      <div className="border-default mb-4 mt-4 rounded-xl border p-4">
        <p className="text-gray-1100 font-medium">{description}</p>
      </div>
      <div className="mb-3 flex flex-col rounded-lg bg-gray-300 px-4 py-3 text-xs">
        {token?.email && <code>user: {token.email}</code>}
        {path && <code>path: {path}</code>}
        {message && <code>error: {message}</code>}
      </div>
      {action}
    </div>
  );
};
